import { useContext, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  Chip,
  Grid,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import SearchBar from '../../../common/search-bar/search-bar';

import FilterBar from '../../../common/filter-bar/filter-bar';
import ChecklistIcon from '@mui/icons-material/Checklist';
import { useImmer } from 'use-immer';
import {
  DoubleDate,
  DoubleDateMode,
  DoubleDateWithMode,
} from '../../../common/double-date-picker/double-date-picker.interfaces';
import DoubleDatePicker from '../../../common/double-date-picker/double-date-picker';
import BulkAction from '../../../common/bulk-action/bulk-action';
import { OrderType, ShopifyOrderValidationResult } from '../../../../interfaces/delivery-company/orders';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import axios from 'axios';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { buildQueryParams } from '../../../common/filter-bar/filter-bar.utils';

import { PaginationContext } from '../../../../contexts/pagination-context';
import { OrgSettingsContext } from '../../../../contexts/org-settings-context';
import { IFilterOption } from '../../../common/filter-bar/types';
import ResponsiveOrdersListing from '../../common/orders-listing';
import { IPickupTask } from '../../../../interfaces/delivery-company/tasks';
import CreateEntityDrawer from '../../../common/create-entity-drawer/create-entity-drawer';
import MerchantCreateOrderForm from '../merchant-create-order-form/merchant-create-order-form';
import { SortingByColumnState } from '../../../common/generic-table/utils';
import { generateDateFilterQueryParam } from '../../../common/double-date-orders-filter/utils';
import DoubleDateOrdersFilter from '../../../common/double-date-orders-filter/double-date-orders-filter';
import { OrdersFilterKeys } from '../../../common/filter-bar/keys-config';
import FrozenOrdersButtonWithDialog from '../../../common/frozen-orders/frozen-orders-dialog';
import MerchantBulkCreateOrders from '../bulk-import/order-bulk-importer';
import { useSnackBar } from '../../../../contexts/snackbar';
import { DeliveryState } from '../../../../enums/orders';
import RequestPickupDialog from '../../../common/request-pickup-dialog';
import { IconWithText } from '../../../common/styles/components';
import { YouTube } from '@mui/icons-material';
import { useVideoTutorial } from '../../../../contexts/video-tutorial-context/video-tutorial-context';
import ShopifyValidationErrors from '../../../common/shopify-validation-errors/shopify-validation-errors';
import NewFeatureChip from '../../../common/new-feature-chip';
import { QuickFilterScope } from '../../../../enums/quick-filters';
import { ApplyQuickFilter } from '../../../common/apply-quick-filters-manager/apply-quick-filters-manager';
import { UnapplyQuickFilter } from '../../../common/apply-quick-filters-manager/apply-quick-filters-manager';
import { IQuickFilter } from '../../../../interfaces/common/quick-filters';

 const relevant_fields = [
        'id',
        'order_id',
        'reference_id',
        'creation_date',
        'customer',
        'address',
        'district',
        'governorate',
        'price',
        'labels',
        'pop',
        'delivery_fee',
        'number_of_packages',
        'delivery_state',
        'description',
        'customer_location',
        'merchant',
        'merchant_location',
        'tracking_hash',
        'workflow',
        'is_exchange',
        'is_critical',
        'is_frozen',
        'is_fragile',
        'public_note',
        'delivery_attempts_count',
        'return_reason',
        'exchange_for',
        'tracking_link',
        'labels',
        'source',
        'updated_by_us',
        'product_price',
      ];

function MerchantOrdersListing() {
  const [ordersList, setOrdersList] = useState<OrderType[]>([]);
  const [ordersCount, setOrdersCount] = useState(0);
  const [incompleteOrdersOnly, setIncompleteOrdersOnly] = useState(false);
  const [includeFrozen, setIncludeFrozen] = useState(false);
  const [requestPickupDialogOpen, setRequestPickupDialogOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState<DoubleDateWithMode>({
    startDate: null,
    endDate: null,
    mode: DoubleDateMode.CREATION_DATE,
  });
  const {showMessage} = useSnackBar()
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { color } = useContext(OrgSettingsContext);
  const [shopifyOrderValidationResult, setShopifyOrderValidationResult] = useState<ShopifyOrderValidationResult[]>([]);
  const [queryEndpointToFetch, setQueryEndpointToFetch] = useState<string>();

  const { showVideoTutorial } = useVideoTutorial();

  const [selectedOrders, updateSelectedOrders] = useImmer<
    Map<string, OrderType>
  >(new Map());

  const resetSelectionState = () => {
    updateSelectedOrders((selectedOrders) => selectedOrders.clear());
  };

  const { user, config } = useContext(AuthContext);

  const { paginationConfig, setPaginationConfig } =
    useContext(PaginationContext);
  const totalSelected = selectedOrders.size;

  const searchOptions = ['Order_Id', 'Ref_Number'];
  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: paginationConfig.rowsPerPage,
    searchColumn: '',
    searchValue: '',
    searchOperation: 'equals',
  });

  const [filters, setFilters] = useState<IFilterOption[]>([]);
  const [sorting, setSorting] = useState<SortingByColumnState>({
    column: 'creation_date',
    order: 'decreasing',
  });


  const getShopifyOrderValidationResult = async () => {
    const response = await axios.get<ShopifyOrderValidationResult[]>(`${API_ENDPOINT}/shopify/v1/invalid-orders/`, config);
    setShopifyOrderValidationResult(response.data);
  }
  useEffect(() => {  
    getShopifyOrderValidationResult();
  }, []);

  useEffect(() => {
      let endpoint = `${API_ENDPOINT}/api/v1/orders/?page=${controller.page + 1}&page_size=${controller.rowsPerPage}&fields=${relevant_fields.join(',')}`;
  
        if (controller.searchColumn !== '' && controller.searchValue !== '') {
          endpoint += `&${controller.searchColumn.toLowerCase()}__${controller.searchOperation}=${encodeURIComponent(controller.searchValue)}`;
        }
  
        const filterQueryParams = buildQueryParams(filters);
        endpoint += `${filterQueryParams}`;
        endpoint += `&${generateDateFilterQueryParam(dateFilter)}`;
        endpoint += `&sort_by_${sorting.order}=${encodeURIComponent(sorting.column)}`;

  
        if (incompleteOrdersOnly) {
          endpoint += `&incomplete_only=true`;
        }
  
        if (includeFrozen) {
          endpoint += `&include_frozen=true`;
        }
  
        setQueryEndpointToFetch(endpoint)
  
    }, [controller, dateFilter, filters, sorting, incompleteOrdersOnly, includeFrozen]);
  

  useEffect(() => {
    if (!queryEndpointToFetch) {
      return 
    }

    const abortController = new AbortController();
    const signal = abortController.signal;

    const getData = async () => {
     

    const endpoint = queryEndpointToFetch
      
    setIsLoading(true)
    try {
        const response = await axios.get<{ count: number; results: OrderType[] }>(
          endpoint,
          { ...config, signal }
        );
        setOrdersList(response.data.results);
        setOrdersCount(response.data.count);
      } catch (error: any) {
        if (axios.isCancel(error) || error.name === 'CanceledError') {
          console.log('Request canceled');
          return; // Exit without setting isLoading to false
        }
        showMessage('Failed to load orders', 'error')
        console.error(error);
      }
      setIsLoading(false);
    };

    getData();

    return () => {
      abortController.abort();
    }
  }, [queryEndpointToFetch]);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPaginationConfig({ ...paginationConfig, rowsPerPage: newRowsPerPage });
    setController({
      ...controller,
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
  };

  const searchQueryAction = (
    column: string,
    value: string,
    operation: string
  ) => {
    setController({
      ...controller,
      page: 0,
      searchColumn: column,
      searchValue: value,
      searchOperation: operation,
    });
  };

  const resetPaginationControls = () => {
    setController({
      ...controller,
      page: 0,
    });
  };
  const filterQueryAction = (filters: IFilterOption[]) => {
    resetPaginationControls();
    setFilters(filters);
    resetSelectionState();
  };

  const dateFilterAction = (dateFilterValue: DoubleDateWithMode) => {
    resetPaginationControls();
    setDateFilter(dateFilterValue);
  };

  const resetAll = () => {
    resetPaginationControls();
    resetSelectionState();
  };

  const canRequestPickup = selectedOrders.size > 0 && Array.from(selectedOrders.values()).every(order => order.delivery_state == DeliveryState.CREATED)

  return (
    <Box>
      <Typography
        variant="h3"
        component="h3"
        sx={{ textAlign: 'center', mt: 3,  }}
      >
        My Orders
        <br />
        <FrozenOrdersButtonWithDialog />
      </Typography>


      {
       <Box sx={ {cursor:'pointer', width: "100%", textDecoration: "underline", color: "primary", display: "flex", justifyContent: "center", alignItems: "center", mb: 3 }} onClick={()=>showVideoTutorial("https://www.youtube.com/embed/HnQTJyfqxSg?si=lQ9xZxZfqZH56NVx")}>
            <IconWithText centered> <YouTube color="primary" /> <Typography  sx={{hover: {color: "primary"}, color: "primary"}} variant="body1">Learn how to request pickup.</Typography></IconWithText>
          </Box>
      }

      <br />

      {
        <Grid container spacing={2}>
          <Grid
            xs={12}
            sx={{
              textAlign: 'right',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            <MerchantBulkCreateOrders />
            
            <CreateEntityDrawer>
              <MerchantCreateOrderForm
                onSuccess={(o) => {
                  if (o.frozen_until) return;
                  if (filters.length === 0)
                    setOrdersList([
                      o,
                      ...ordersList.slice(
                        0,
                        Math.max(0, ordersList.length - 1)
                      ),
                    ]);
                }}
              />
            </CreateEntityDrawer>
          </Grid>

          <Grid xs={12}>
            {shopifyOrderValidationResult.length > 0 && <ShopifyValidationErrors shopifyOrderValidationResult={shopifyOrderValidationResult} />}
            <Box>
              <Accordion elevation={0}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                >
                  <Box>
                    <Typography
                    variant="h5"
                    component="h5"
                    sx={{ textAlign: 'center', mt: 1, mb: 1 }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                      }}
                    >
                      <ManageSearchIcon fontSize="large" color="primary" />
                      <span>
                        <b>Search and Filter Controls</b>
                      </span>
                    </div>
                  </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <SearchBar
                    searchOptions={searchOptions}
                    searchQueryAction={searchQueryAction}
                  />
                  <br />

                  <DoubleDateOrdersFilter dateFilterAction={dateFilterAction} />
                  <br />

                  <FilterBar
                    filterQueryAction={filterQueryAction}
                    currentlySelectedFilters={filters}
                    filterKeys={OrdersFilterKeys('merchant')}
                    quickFiltersScope={QuickFilterScope.ORDER}
                  />
                </AccordionDetails>
              </Accordion>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 10,
                  marginLeft: 10,
                }}
              >
                <Chip
                  icon={<ChecklistIcon />}
                  color="primary"
                  label={`Selected ${totalSelected}/${ordersCount}`}
                  variant={
                    totalSelected === ordersCount && totalSelected > 0
                      ? 'filled'
                      : 'outlined'
                  }
                />

                <BulkAction
                  selectedOrders={selectedOrders}
                  bulkEndpoint={`${API_ENDPOINT}/api/merchant/orders/bulk-update/`}
                  refetchOrders={resetAll}
                />
              </div>
              <Switch
                checked={incompleteOrdersOnly}
                onChange={(e) => setIncompleteOrdersOnly(!incompleteOrdersOnly)}
              />{' '}
              Incomplete orders only
              <br />
              <Switch
                checked={includeFrozen}
                onChange={(e) => setIncludeFrozen(!includeFrozen)}
              />{' '}
              Include Frozen Orders
              <br />
              {
                canRequestPickup && <Box display='flex' justifyContent='center' width='100%' >
                      <Button variant='contained' color='primary' onClick={() => setRequestPickupDialogOpen(true)}>
                        <ChecklistIcon sx={{ mr: 1 }} />
                        Request Pickup
                      </Button>
                  </Box>
              }
              <ResponsiveOrdersListing
                ordersList={ordersList}
                selectedOrders={selectedOrders}
                updateSelectedOrders={updateSelectedOrders}
                isLoading={isLoading}
                sorting={sorting}
                setSorting={setSorting}
                applyQuickFilter={(quickFilter: IQuickFilter | null) => ApplyQuickFilter(quickFilter, filters, setFilters)}
                sourceView="merchant-orders-listing"
              />
              <TablePagination
                component="div"
                onPageChange={handlePageChange}
                page={controller.page}
                count={ordersCount}
                rowsPerPage={controller.rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 250, 500, 1000]}
                labelRowsPerPage=""
                style={{ overflow: 'visible' }}
              />
            </Box>
          </Grid>
        </Grid>
      }
      <RequestPickupDialog orders={Array.from(selectedOrders.values())} onClose={() => setRequestPickupDialogOpen(false)} open={requestPickupDialogOpen} />
    </Box>
  );
}

export default MerchantOrdersListing;
